import {useFormContext, useWatch} from 'react-hook-form'
import {UserEducationFormValidationSchema} from '@/features/users/types'
import {useTranslation} from '@/translations/i18n'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Button} from '@/components/ui/button/Button'
import {DotsHorizontalIcon, Pencil01Icon, PlusIcon, Trash01Icon} from '@/components/ui/icon/Icon'
import {
    StyledQualificationEmptyBox,
    StyledQualificationParagraph,
    StyledQualificationRowSide,
    StyledQualificationSubTitle,
    StyledQualificationTitle,
    StyledUserQualificationEmptyBoxSubtitle,
    StyledUserQualificationEmptyBoxTitle
} from './style'
import {userQualificationToLabel} from '@/features/users/utils'
import {MenuButton, MenuProvider} from '@ariakit/react'
import {Menu, MenuItem} from '@/components/ui/menu-atoms/MenuAtoms'
import {Divider} from '@/components/ui/divider/Divider'
import {useTheme} from 'styled-components'

export const UserQualificationsList = ({
    setIsQualificationFormModalOpen,
    setSelectedQualification
}: {
    setSelectedQualification?: (value: number) => void
    setIsQualificationFormModalOpen?: (value: boolean) => void
}) => {
    const {t} = useTranslation()
    const theme = useTheme()

    // methods
    const form = useFormContext<UserEducationFormValidationSchema>()

    // form observer
    const qualifications = useWatch({control: form.control, name: 'qualifications'})

    if (!qualifications || qualifications.length == 0) {
        return (
            <StyledQualificationEmptyBox direction="column" align="center" justify="center" gap={4} fullWidth>
                <Flexbox direction="column" gap={1} fullWidth align="center" justify="center">
                    <StyledUserQualificationEmptyBoxTitle>
                        {t('users:no_qualifications')}
                    </StyledUserQualificationEmptyBoxTitle>
                    <StyledUserQualificationEmptyBoxSubtitle>
                        {t('users:add_qualification_hint')}
                    </StyledUserQualificationEmptyBoxSubtitle>
                </Flexbox>
                {setIsQualificationFormModalOpen && (
                    <Button size="sm" variant="secondary" onClick={() => setIsQualificationFormModalOpen(true)}>
                        <PlusIcon />
                        {t('commons:add')}
                    </Button>
                )}
            </StyledQualificationEmptyBox>
        )
    }

    return qualifications.map((qualification, index) => (
        <Flexbox key={index} direction="column" gap={2} fullWidth>
            <Flexbox key={index} fullWidth>
                <Flexbox direction="column" fullWidth gap={1}>
                    <StyledQualificationTitle>
                        <b>{t(userQualificationToLabel[qualification.type])}</b> {qualification.sector}
                    </StyledQualificationTitle>
                    <StyledQualificationSubTitle>{qualification.institute}</StyledQualificationSubTitle>
                    <StyledQualificationParagraph>{qualification.otherInformation}</StyledQualificationParagraph>
                </Flexbox>
                <StyledQualificationRowSide gap={2} align="center">
                    <p>{qualification.graduationYear}</p>
                    {setSelectedQualification && (
                        <MenuProvider placement="bottom-end">
                            <MenuButton
                                render={
                                    <Button size="sm" variant="ghost" shape="square">
                                        <DotsHorizontalIcon />
                                    </Button>
                                }
                            />
                            <Menu>
                                <MenuItem
                                    render={<Button variant="ghost" onClick={() => setSelectedQualification(index)} />}
                                >
                                    <Pencil01Icon />
                                    {t('commons:edit')}
                                </MenuItem>
                                <Divider direction="horizontal" />
                                <MenuItem
                                    style={{color: theme.palette.danger['600']}}
                                    render={
                                        <Button variant="ghost" style={{width: '100%', justifyContent: 'flex-start'}} />
                                    }
                                    onClick={() => {
                                        form.setValue(
                                            'qualifications',
                                            qualifications.filter((_, q_index) => q_index != index),
                                            {
                                                shouldDirty: true
                                            }
                                        )
                                    }}
                                >
                                    <Trash01Icon style={{fill: theme.palette.danger['600']}} />
                                    {t('commons:delete')}
                                </MenuItem>
                            </Menu>
                        </MenuProvider>
                    )}
                </StyledQualificationRowSide>
            </Flexbox>

            {index < qualifications.length - 1 && <Divider direction="horizontal" />}
        </Flexbox>
    ))
}
